import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  Shield02Icon, 
  CreditCardIcon, 
  CheckmarkCircle01Icon,
  InformationCircleIcon,
  Copy01Icon,
  Image01Icon,
  AttachmentIcon,
  AlertCircleIcon
} from '@hugeicons/core-free-icons';
import Button from '../../components/Button';
import { api } from '../../utils/api';
import { FLAGS } from '../../config/flags';

const AgentOnboardingFee = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState(1); // 1: Details, 2: Receipt, 3: Success
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [depositUid, setDepositUid] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPaymentDetails();
  }, []);

  const fetchPaymentDetails = async () => {
    if (FLAGS.USE_MOCK_API) {
      setPaymentDetails({
        admin_account: {
          deriv_cr_number: 'CR98765432',
          payment_instructions: 'Transfer to this CR account via Deriv P2P or Internal Transfer.',
          expected_amount: '10.00',
          currency: 'USD'
        },
        deposit: { uid: 'mock-deposit-uid' }
      });
      setDepositUid('mock-deposit-uid');
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/agent/verification/payment-details');
      setPaymentDetails(response.data);
      setDepositUid(response.data.deposit?.uid);
      
      // Prevent double submission: If already pending, go back to dashboard where banner is shown
      if (response.data.deposit?.status === 'pending_verification') {
        alert("Your verification is already under review.");
        navigate('/dashboard');
      } else if (response.data.deposit?.status === 'initiated') {
        // If initiated but not paid/uploaded, stay on step 1 (or 2 if we could detect partial)
        // Logic remains as is for new/initiated
      }
    } catch (err) {
      console.error('Failed to fetch payment details:', err);
      setError(err.message || "Failed to load payment details.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!depositUid) return;
    setProcessing(true);

    if (FLAGS.USE_MOCK_API) {
      setTimeout(() => {
        setStep(2);
        setProcessing(false);
      }, 1000);
      return;
    }

    try {
      await api.post(`/agent/verification/${depositUid}/confirm-payment`, {
        amount_sent: parseFloat(paymentDetails.deposit?.expected_amount || 10.00),
        agent_note: 'Paid verification fee'
      });
      setStep(2);
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleUploadReceipt = async () => {
    if (!receiptFile || !depositUid) return;
    setProcessing(true);

    const formData = new FormData();
    formData.append('receipt', receiptFile);

    if (FLAGS.USE_MOCK_API) {
      setTimeout(() => {
        setStep(3);
        setProcessing(false);
        setTimeout(() => navigate('/dashboard'), 2000);
      }, 1500);
      return;
    }

    try {
      await api.postFile(`/agent/verification/${depositUid}/upload-receipt`, formData);
      setStep(3);
      setTimeout(() => navigate('/dashboard'), 2500);
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-soft-green"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-off-white dark:bg-dark-bg">
        <div className="max-w-md w-full bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-8 rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-float-lg text-center space-y-6">
          <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-full inline-block text-red-500">
            <HugeiconsIcon icon={AlertCircleIcon} size={48} />
          </div>
          <h2 className="text-2xl font-black text-charcoal dark:text-white">Configuration Error</h2>
          <p className="text-muted-gray">{error}</p>
          <Button onClick={() => navigate('/support')} className="w-full py-4 rounded-2xl">
            Contact Support
          </Button>
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="w-full py-2 text-sm text-muted-gray">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (step === 3) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-off-white dark:bg-dark-bg">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-full inline-block animate-bounce">
            <HugeiconsIcon icon={CheckmarkCircle01Icon} size={48} className="text-soft-green" />
          </div>
          <h2 className="text-3xl font-black text-charcoal dark:text-white">Verification Submitted!</h2>
          <p className="text-muted-gray">Your token is being verified. You will have full agent access shortly. Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-off-white dark:bg-dark-bg">
      <div className="max-w-lg w-full bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-8 rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-float-lg">
        <div className="text-center mb-8">
          <div className="bg-blue-500/10 p-5 rounded-3xl inline-block mb-6 text-blue-500">
            <HugeiconsIcon icon={Shield02Icon} size={40} />
          </div>
          <h1 className="text-2xl font-black text-charcoal dark:text-white mb-2">Refundable Verification Token</h1>
          <p className="text-muted-gray">Step {step} of 2: {step === 1 ? 'Token Details' : 'Upload Receipt'}</p>
        </div>

        {step === 1 && (
          <div className="space-y-6">
            <div className="bg-off-white/50 dark:bg-gray-800/50 p-6 rounded-3xl border border-light-gray/20 dark:border-gray-700/20">
              <div className="flex justify-between items-center mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                <span className="text-sm font-bold text-muted-gray uppercase tracking-widest">Amount Due</span>
                <span className="text-3xl font-black text-charcoal dark:text-white">
                  ${paymentDetails?.deposit?.expected_amount || paymentDetails?.admin_account?.expected_amount || '10.00'}
                </span>
              </div>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold text-muted-gray">Admin Deriv Account</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-charcoal dark:text-white bg-white dark:bg-gray-700 px-2 py-1 rounded">
                      {paymentDetails?.admin_account?.deriv_cr_number || 'Loading...'}
                    </span>
                    <button 
                      onClick={() => {navigator.clipboard.writeText(paymentDetails?.admin_account?.deriv_cr_number); alert('Copied!')}}
                      className="text-soft-green"
                    >
                      <HugeiconsIcon icon={Copy01Icon} size={16} />
                    </button>
                  </div>
                </div>
                
                {paymentDetails?.admin_account?.payment_instructions && (
                  <div className="text-xs text-muted-gray bg-blue-50 dark:bg-blue-900/10 p-3 rounded-xl border border-blue-100 dark:border-blue-800/20">
                    {paymentDetails.admin_account.payment_instructions}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-yellow-50 dark:bg-yellow-900/10 rounded-2xl border border-yellow-100 dark:border-yellow-800/30">
              <HugeiconsIcon icon={InformationCircleIcon} size={18} className="text-yellow-600 mt-0.5 shrink-0" />
              <p className="text-xs text-yellow-800 dark:text-yellow-200 leading-relaxed font-medium">
                Please transfer the exact amount to the account above via Deriv P2P or Internal Transfer, then click confirm.
              </p>
            </div>

            <Button 
              onClick={handleConfirmPayment} 
              disabled={processing || !depositUid}
              className="w-full py-4 rounded-2xl text-lg font-black shadow-float gap-2"
            >
              {processing ? 'Processing...' : (
                <>
                  <HugeiconsIcon icon={CreditCardIcon} size={20} />
                  I Have Sent the Money
                </>
              )}
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
             <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800/30 p-4 rounded-2xl flex items-center gap-3">
               <HugeiconsIcon icon={CheckmarkCircle01Icon} size={24} className="text-green-600" />
               <p className="text-sm font-bold text-green-800 dark:text-green-200">Payment confirmed. Please upload proof.</p>
             </div>

             <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-3xl p-8 text-center hover:border-soft-green transition-colors cursor-pointer bg-gray-50 dark:bg-gray-800/30">
                <input 
                  type="file" 
                  id="receipt-upload" 
                  className="hidden" 
                  accept="image/*,.pdf"
                  onChange={(e) => setReceiptFile(e.target.files[0])}
                />
                <label htmlFor="receipt-upload" className="cursor-pointer flex flex-col items-center">
                  <div className="w-16 h-16 bg-white dark:bg-gray-700 rounded-full flex items-center justify-center mb-4 shadow-sm">
                    <HugeiconsIcon icon={receiptFile ? Image01Icon : AttachmentIcon} size={32} className="text-soft-green" />
                  </div>
                  <span className="font-bold text-charcoal dark:text-white text-lg">
                    {receiptFile ? receiptFile.name : 'Click to Upload Receipt'}
                  </span>
                  <span className="text-xs text-muted-gray mt-2">JPG, PNG or PDF (Max 5MB)</span>
                </label>
             </div>

             <Button 
              onClick={handleUploadReceipt} 
              disabled={!receiptFile || processing}
              className="w-full py-4 rounded-2xl text-lg font-black shadow-float gap-2"
            >
              {processing ? 'Uploading...' : 'Submit Verification'}
            </Button>
          </div>
        )}

      </div>
    </div>
  );
};

export default AgentOnboardingFee;
